import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { storedAttribution } from '../lib/attribution'
import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'

export interface VerifiedCarrier {
  dotNumber: string
  legalName: string
  city?: string | null
  state?: string | null
  powerUnits?: number | null
  /** Under five trucks: the standard application suits this carrier better. */
  smallFleet: boolean
}

export interface PacketSubmissionResult {
  /** Always the same message regardless of whether the email or DOT was already known. */
  detail: string
}

/**
 * Verify a DOT against CAB and return the carrier it resolves to. Creates no customer
 * records — deliberately not `web/start`, which also writes a stub user, org,
 * application, and synthetic driver/truck rows we don't want for a packet submission.
 * A found DOT still counts as the large-fleet form's DOT entry for the funnel.
 */
export async function verifyDotNumber(dotNumber: string): Promise<VerifiedCarrier> {
  const response = await axios.post('/api/v1/insure/insurance-application/web/verify-dot/', {
    dot_number: dotNumber,
    attribution: storedAttribution(),
  })
  return toCamelCase(response.data) as VerifiedCarrier
}

/** Submit the packet. Response is intentionally uniform so it can't be used to probe emails or DOTs. */
export async function submitLargeFleetPacket(submission: {
  fullName: string
  email: string
  dotNumber: string
  travelRange: string[]
  files: File[]
  wantsGroupCaptive: boolean
}): Promise<PacketSubmissionResult> {
  const form = new FormData()
  form.append('full_name', submission.fullName)
  form.append('email', submission.email)
  form.append('dot_number', submission.dotNumber)
  submission.travelRange.forEach((value) => form.append('travel_distance', value))
  submission.files.forEach((file) => form.append('file', file))
  if (submission.wantsGroupCaptive) form.append('group_captive', 'true')

  const response = await axios.post('/api/v1/insure/insurance-application/web/packet/', form)
  return toCamelCase(response.data) as PacketSubmissionResult
}

export type PacketConfirmationStatus = 'confirmed' | 'expired' | 'not_found'

/** Confirm a staged packet from its emailed link. */
export async function confirmLargeFleetPacket(token: string): Promise<PacketConfirmationStatus> {
  try {
    const response = await axios.post('/api/v1/insure/insurance-application/web/packet/confirm/', { token })
    return (response.data as { detail: PacketConfirmationStatus }).detail
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 400) return 'expired'
    return 'not_found'
  }
}

/** A packet the customer sent and confirmed, as staff see it on the customer page. */
export interface ConfirmedLargeFleetPacket {
  id: string
  fullName: string
  email: string
  travelRange: string[]
  wantsGroupCaptive: boolean
  fileCount: number
  confirmedAt: string
}

/** Staff only. Empty for a customer who never sent a large-fleet packet. */
export function useCustomerLargeFleetPackets(orgId: string) {
  return useQuery({
    queryKey: [CacheKeys.ORGANISATIONS, orgId, 'large-fleet-packets'],
    queryFn: () =>
      axios
        .get(`/api/v1/insure/organisations/${orgId}/large-fleet-packets/`)
        .then((response) => toCamelCase(response.data) as ConfirmedLargeFleetPacket[]),
  })
}
